import { type FC, type MouseEvent, useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import { useTranslation } from 'react-i18next';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import ActionCell from '__components/Tables/ActionCell';
import { KizenTypography } from '__app/typography';
import { FILES_TABLE_ACTIONS } from '__pages/KnowledgeSettings/constants';
import { type KnowledgeSource } from '@kizen/api/knowledge';
import { getProcessingStatusesConfig } from 'pages/KnowledgeSettings/helpers';
import { StyledStatus } from 'pages/KnowledgeSettings/styles';

const EMPTY_VALUE = '—';

const ControlsCell: FC<{
  data?: KnowledgeSource;
  canWrite?: boolean;
  onSelectAction: (
    action: { value: keyof typeof FILES_TABLE_ACTIONS },
    data: KnowledgeSource
  ) => void;
}> = ({ data, canWrite, onSelectAction, ...props }) => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      [
        canWrite && {
          label: t('Delete'),
          value: FILES_TABLE_ACTIONS.delete,
        },
        {
          label: t('Download'),
          value: FILES_TABLE_ACTIONS.download,
        },
      ].filter(Boolean),
    [canWrite, t]
  );
  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

const StatusCell: FC<{
  status: KnowledgeSource['processing_status'];
  onClickStatus: () => void;
  hasDetails?: boolean;
}> = ({ status, onClickStatus, hasDetails }) => {
  const { t } = useTranslation();
  const { label = EMPTY_VALUE, color = null } = useMemo(
    () => getProcessingStatusesConfig(t)[status] || {},
    [t, status]
  );
  const anchorProps = hasDetails
    ? {
        onClick: (e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          onClickStatus();
        },
        type: 'anchor',
        href: '',
      }
    : {};
  return (
    <StyledStatus color={color} {...anchorProps}>
      {label}
    </StyledStatus>
  );
};

type useColumnsProps = {
  onSelectAction: (
    action: { value: keyof typeof FILES_TABLE_ACTIONS },
    data: KnowledgeSource
  ) => void;
  onShowDetails: (data: KnowledgeSource) => void;
  isOnlineSources?: boolean;
  canWrite?: boolean;
};

export const useColumns = ({
  onSelectAction,
  onShowDetails,
  isOnlineSources,
  canWrite,
}: useColumnsProps) => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="15px" />,
        cell: <SizedCell width="15px" />,
        format: () => <span />,
      },
      {
        id: 'description',
        headCell: (
          <SortableHeadCell
            label={t('Description')}
            width="230px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="230px" padding="30px" />,
        format: (description: KnowledgeSource['description']) => {
          return (
            <TextEllipsisWithTooltip>
              {description || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          );
        },
      },
      isOnlineSources
        ? {
            id: 'pages_count',
            headCell: (
              <SortableHeadCell
                hasSorting={false}
                label={t('# Pages')}
                width="120px"
                padding="30px"
              />
            ),
            cell: <SizedCell width="120px" padding="30px" />,
            format: (_: never, data: KnowledgeSource) => {
              return (
                <TextEllipsisWithTooltip>
                  {data.s3_object.content_type || EMPTY_VALUE}
                </TextEllipsisWithTooltip>
              );
            },
          }
        : {
            id: 'file_type',
            headCell: (
              <SortableHeadCell
                hasSorting={false}
                label={t('File Type')}
                width="120px"
                padding="30px"
              />
            ),
            cell: <SizedCell width="120px" padding="30px" />,
            format: (fileType: string, data: KnowledgeSource) => {
              return (
                <TextEllipsisWithTooltip>
                  {fileType || data.s3_object?.content_type || EMPTY_VALUE}
                </TextEllipsisWithTooltip>
              );
            },
          },
      {
        id: 'updated',
        headCell: (
          <SortableHeadCell
            numeric
            label={t('Updated')}
            width="155px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="155px" padding="30px" />,
        format: (updated: KnowledgeSource['updated']) => {
          return updated ? (
            <DateTimeInputInline readOnly value={updated} showDateTooltip />
          ) : (
            <KizenTypography>{EMPTY_VALUE}</KizenTypography>
          );
        },
      },
      {
        id: 'processing_status',
        headCell: (
          <SortableHeadCell label={t('Status')} width="120px" padding="30px" />
        ),
        cell: <SizedCell width="120px" padding="30px" />,
        format: (
          status: KnowledgeSource['processing_status'],
          data: KnowledgeSource
        ) => {
          const onClickStatus = () => {
            onShowDetails(data);
          };
          return (
            <StatusCell
              status={status}
              onClickStatus={onClickStatus}
              hasDetails={!!data.processing_details}
            />
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Actions')}
            width="50px"
            padding="15px"
            align="right"
          />
        ),
        cell: <ControlsCell onSelectAction={onSelectAction} canWrite={canWrite} />,
      },
    ],
    [onSelectAction, onShowDetails, isOnlineSources, canWrite, t]
  );
};
